import { create } from "zustand";
import { persist } from "zustand/middleware";
import { logger } from "../../utils";
// ─────────────────────────────────────────────────────────────
// TOKEN STORAGE NOTE (Security Debt: TASK-P0-03)
// ─────────────────────────────────────────────────────────────
// Saat ini token disimpan di localStorage via Zustand persist.
// Risiko: rentan XSS jika ada script injection.
//
// Mitigasi aktif:
//   1. clearSession() membuang token dari state DAN memanggil
//      storage.removeItem() secara eksplisit untuk memastikan
//      localStorage benar-benar bersih (belt-and-suspenders).
//   2. setSession() memvalidasi bahwa token tidak kosong sebelum
//      disimpan — mencegah penyimpanan state yang korup/tidak valid.
//   3. Setiap event sesi kritis di-log untuk auditability.
//
// Migration path (future):
//   Untuk migrasi ke httpOnly cookie, ganti persist storage
//   dengan custom storage adapter yang mengarah ke backend
//   session endpoint. Flag: PENA_AUTH_USE_COOKIE_SESSION = false
// ─────────────────────────────────────────────────────────────
const AUTH_STORAGE_KEY = "auth-storage";
export const useAuthStore = create()(persist((set) => ({
    user: null,
    lembaga: null,
    accessToken: null,
    accessExpiredAt: null,
    refreshToken: null,
    refreshExpiredAt: null,
    hydrated: false,
    authStage: "IDLE",
    mfaToken: null,
    mfaChallengeId: null,
    currentCabang: undefined,
    availableCabang: [],
    activeModules: [],
    setCabang: (cabang) => set({ currentCabang: cabang }),
    setHydrated: (value) => set({ hydrated: value }),
    setMfaEnabled: (status) => set((state) => ({
        user: state.user ? { ...state.user, mfaEnabled: status } : null
    })),
    updateUser: (userData) => set((state) => ({
        user: state.user ? { ...state.user, ...userData } : null
    })),
    updateCabangName: (idCabang, namaCabang) => set((state) => ({
        // Jika cabang yang diedit adalah cabang yang sedang aktif, update label navbar
        currentCabang: state.availableCabang?.find((c) => c.idCabang === idCabang)
            ?.namaCabang === state.currentCabang
            ? namaCabang
            : state.currentCabang,
        // Update nama di daftar availableCabang (dropdown navbar)
        availableCabang: state.availableCabang?.map((c) => c.idCabang === idCabang ? { ...c, namaCabang } : c),
    })),
    setMfaPending: (mfaToken, challengeId) => {
        logger.info("[AUTH] Memasuki tahap verifikasi MFA.", { challengeId });
        set({
            authStage: "MFA_PENDING",
            mfaToken,
            mfaChallengeId: challengeId || null,
            accessToken: null,
            accessExpiredAt: null,
            refreshToken: null,
            refreshExpiredAt: null,
        });
    },
    setSession: (data) => {
        // Guard: tolak penyimpanan jika accessToken tidak valid
        if (!data.accessToken) {
            logger.warn("[AUTH] setSession dipanggil dengan token kosong — sesi tidak disimpan.", { hasAccessToken: !!data.accessToken });
            return;
        }
        logger.info("[AUTH] Sesi dimulai.", {
            userId: data.user?.username,
            idLembaga: data.lembaga?.idLembaga,
            accessExpiredAt: data.accessExpiredAt,
        });
        let activeModules = [];
        try {
            const payload = JSON.parse(atob(data.accessToken.split('.')[1]));
            if (payload.activeModules && Array.isArray(payload.activeModules)) {
                activeModules = payload.activeModules;
            }
        }
        catch (e) {
            logger.warn("[AUTH] Failed to parse JWT payload for activeModules");
        }
        set({
            authStage: "AUTHENTICATED",
            user: data.user,
            lembaga: data.lembaga,
            accessToken: data.accessToken,
            accessExpiredAt: data.accessExpiredAt,
            refreshToken: data.refreshToken || null,
            refreshExpiredAt: data.refreshExpiredAt || null,
            currentCabang: data.lembaga?.cabang?.namaCabang,
            availableCabang: data.user?.availableCabangList || [],
            activeModules, // <--- Add to state
            // Reset MFA state upon successful authentication
            mfaToken: null,
            mfaChallengeId: null,
        });
    },
    clearSession: () => {
        logger.info("[AUTH] Sesi dihapus — token dan data pengguna dibersihkan.");
        // Belt-and-suspenders: hapus storage key secara eksplisit.
        // Zustand persist akan mengupdate storage saat state berubah,
        // tapi removeItem memastikan tidak ada sisa data di localStorage.
        try {
            localStorage.removeItem(AUTH_STORAGE_KEY);
        }
        catch {
            // Diabaikan: environment tanpa localStorage (SSR/test)
        }
        set({
            authStage: "IDLE",
            user: null,
            lembaga: null,
            accessToken: null,
            accessExpiredAt: null,
            refreshToken: null,
            refreshExpiredAt: null,
            currentCabang: undefined,
            availableCabang: [],
            activeModules: [],
            mfaToken: null,
            mfaChallengeId: null,
        });
    },
}), {
    name: AUTH_STORAGE_KEY,
    partialize: (state) => ({
        user: state.user,
        lembaga: state.lembaga,
        currentCabang: state.currentCabang,
        availableCabang: state.availableCabang,
        activeModules: state.activeModules,
        authStage: state.authStage,
    }),
    onRehydrateStorage: () => (state) => {
        if (state)
            state.setHydrated(true);
    },
}));
